import { memo } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { green, red } from '../../assets/colors'

const FOOTER_RANGE_INFO = 'Total includes only strikes visible on the chart'

export type OiAnalysisFooterProps = {
    isOIChangeScreen: boolean
    footerDateLabel: string
    totalCallOi: number
    totalPutOi: number
    callOiChange: number
    putOiChange: number
    compact?: boolean
}

const FooterMetric = memo(({ label, value, signed }: { label: string; value: number; signed?: boolean }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, flexWrap: 'wrap' }}>
        <Typography variant="body2" component="span" color="text.secondary">
            {label}
        </Typography>
        <Tooltip title={FOOTER_RANGE_INFO}>
            <InfoOutlinedIcon
                sx={{ fontSize: '0.95rem', opacity: 0.55, cursor: 'help', verticalAlign: 'middle' }}
                aria-label={FOOTER_RANGE_INFO}
            />
        </Tooltip>
        <Typography
            variant="body2"
            component="span"
            fontWeight="bold"
            color={signed ? (value >= 0 ? green : red) : undefined}
        >
            {signed && value >= 0 ? '+' : ''}
            {(value / 10000000).toFixed(2)}Cr
        </Typography>
    </Box>
))

FooterMetric.displayName = 'FooterMetric'

const OiAnalysisFooter = ({
    isOIChangeScreen,
    footerDateLabel,
    totalCallOi,
    totalPutOi,
    callOiChange,
    putOiChange,
    compact = false,
}: OiAnalysisFooterProps) => (
    <Box
        sx={{
            px: compact ? 1 : 2,
            mb: compact ? 1 : 2,
            display: 'flex',
            justifyContent: 'center',
            gap: compact ? 1.5 : 3,
            alignItems: 'center',
            flexWrap: 'wrap',
        }}
    >
        <Typography variant="body2" color="text.secondary">
            {isOIChangeScreen ? `OI Change on ${footerDateLabel}` : `Open Interest on ${footerDateLabel}`}
        </Typography>
        {isOIChangeScreen ? (
            <>
                <FooterMetric label="Total Call OI change" value={callOiChange} signed />
                <FooterMetric label="Total Put OI change" value={putOiChange} signed />
            </>
        ) : (
            <>
                <FooterMetric label="Total Call OI" value={totalCallOi} />
                <FooterMetric label="Total Put OI" value={totalPutOi} />
            </>
        )}
    </Box>
)

export default memo(OiAnalysisFooter)
